import { addDays, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns";
import { fmtZone } from "@/lib/format";
import type { Post, PostType } from "@/lib/types";

/** When a post sits on the calendar: its schedule, else its publish time, else creation. */
export function postWhen(p: Post): string {
  const pub = p.publications[0];
  return pub?.scheduledAt ?? pub?.publishedAt ?? p.createdAt;
}

/** A time zone in plain words (ALP-84): the one formatter is `fmtZone` in lib/format (slice B). */
export const zoneLabel = (tz: string, lang: "en" | "ar" = "en", at: Date = new Date()): string => fmtZone(tz, lang, at);

export type PostVisualStatus = "published" | "scheduled" | "draft" | "failed" | "publishing";

export function postStatus(p: Post): PostVisualStatus {
  if (p.status === "failed" || p.publications.some((x) => x.status === "failed")) return "failed";
  if (p.publications.some((x) => x.status === "publishing")) return "publishing";
  if (p.status === "published" || p.status === "partially_published") return "published";
  if (p.status === "scheduled" || p.publications.some((x) => x.status === "scheduled")) return "scheduled";
  return "draft";
}

export function canMove(p: Post): boolean {
  const s = postStatus(p);
  return s === "scheduled" || s === "draft" || s === "failed";
}

/** Counters that Instagram enforces on a caption. */
export function captionStats(text: string) {
  return {
    chars: [...text].length,
    hashtags: (text.match(/#[\p{L}\p{N}_]+/gu) ?? []).length,
    mentions: (text.match(/@[\w.]+/g) ?? []).length,
  };
}

export const LIMITS = { chars: 2200, hashtags: 30, mentions: 20 } as const;

/** Week start: Saturday for Arabic (Levant/Gulf business week), Monday for English. */
export const weekStartsOn = (lang: "ar" | "en"): 0 | 1 | 6 => (lang === "ar" ? 6 : 1);

export function monthGrid(anchor: Date, lang: "ar" | "en"): Date[] {
  const start = startOfWeek(startOfMonth(anchor), { weekStartsOn: weekStartsOn(lang) });
  const end = endOfWeek(endOfMonth(anchor), { weekStartsOn: weekStartsOn(lang) });
  const days: Date[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  return days;
}

export function weekDays(anchor: Date, lang: "ar" | "en"): Date[] {
  const start = startOfWeek(anchor, { weekStartsOn: weekStartsOn(lang) });
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const hm = (d: Date) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

export function parseYmd(s: string | undefined): Date {
  if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number) as [number, number, number];
    return new Date(y, m - 1, d);
  }
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

/** Combine a local date (yyyy-mm-dd) and time (hh:mm) into an ISO string. */
export function toIso(date: string, time: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const [hh, mm] = time.split(":").map(Number) as [number, number];
  return new Date(y, m - 1, d, hh, mm).toISOString();
}

/** The next time a weekly slot (day 0 = Sunday, hour) occurs after `from`. */
export function nextSlot(day: number, hour: number, from = new Date()): Date {
  const d = new Date(from);
  d.setMinutes(0, 0, 0);
  d.setHours(hour);
  let diff = (day - d.getDay() + 7) % 7;
  if (diff === 0 && d <= from) diff = 7;
  return addDays(d, diff);
}

export const isVideo = (mime: string) => mime.startsWith("video/");

export type Crop = "square" | "portrait" | "landscape" | "vertical";
export const CROP_RATIO: Record<Crop, string> = { square: "1 / 1", portrait: "4 / 5", landscape: "1.91 / 1", vertical: "9 / 16" };
export const cropsFor = (type: PostType): Crop[] => (type === "reel" || type === "story" ? ["vertical"] : ["square", "portrait", "landscape"]);

export function metric(p: { metrics: { key: string; value: number | null }[] }, key: string): number | null {
  return p.metrics.find((m) => m.key === key)?.value ?? null;
}

export const STATUS_TONE: Record<PostVisualStatus, "ok" | "accent" | "neutral" | "bad" | "info"> = {
  published: "ok",
  scheduled: "accent",
  draft: "neutral",
  failed: "bad",
  publishing: "info",
};
